// frontend/src/components/CampaignManager/dialogs/BulkCopyDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  Stack
} from '@mui/material';
import { Lottery } from '../types';

interface Props {
  open: boolean;
  title: string;
  itemType: 'campaign' | 'action';
  selectedCount: number;
  currentLotteryId?: number;
  lotteries: Lottery[];
  onClose: () => void;
  onCopy: (targetLotteryId: number, copyActions?: boolean) => Promise<void>;
}

export default function BulkCopyDialog({
  open,
  title,
  itemType,
  selectedCount,
  currentLotteryId,
  lotteries,
  onClose,
  onCopy
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedLotteryId, setSelectedLotteryId] = useState<number>(0);
  const [copyActions, setCopyActions] = useState(true);
  const [copying, setCopying] = useState(false);

  // Filter out the current lottery
  const availableLotteries = lotteries.filter(l => l.id !== currentLotteryId);

  const handleCopy = async () => {
    if (selectedLotteryId === 0) return;

    setCopying(true);
    try {
      await onCopy(selectedLotteryId, itemType === 'campaign' ? copyActions : undefined);
      onClose();
      setSelectedLotteryId(0);
      setCopyActions(true);
    } catch (error) {
      console.error('Error copying:', error);
      alert('Fehler beim Kopieren');
    } finally {
      setCopying(false);
    }
  };

  const handleClose = () => {
    if (!copying) {
      setSelectedLotteryId(0);
      setCopyActions(true);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Ausgewählt:
            </Typography>
            <Chip
              label={`${selectedCount} ${itemType === 'campaign' ? 'Kampagne(n)' : 'Aktion(en)'}`}
              color="primary"
              sx={{ mt: 0.5 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Die ausgewählten Elemente werden in die Ziel-Lotterie kopiert.
            Alle Einstellungen bleiben erhalten, nur die Lotterie-Zuordnung wird angepasst.
            {itemType === 'action' && ' Die WKZ werden neu generiert.'}
          </Typography>

          <FormControl fullWidth required>
            <InputLabel>Ziel-Lotterie</InputLabel>
            <Select
              value={selectedLotteryId}
              onChange={(e) => setSelectedLotteryId(Number(e.target.value))}
              label="Ziel-Lotterie"
              disabled={copying}
            >
              <MenuItem value={0}>-- Bitte wählen --</MenuItem>
              {availableLotteries.map(lottery => (
                <MenuItem key={lottery.id} value={lottery.id}>
                  {lottery.name} ({lottery.type} {lottery.year})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {itemType === 'campaign' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={copyActions}
                  onChange={(e) => setCopyActions(e.target.checked)}
                  disabled={copying}
                />
              }
              label="Zugehörige Aktionen ebenfalls kopieren"
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={copying}>
          Abbrechen
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={copying || selectedLotteryId === 0}
        >
          {copying ? 'Kopiere...' : `${selectedCount} kopieren`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
